// Header.tsx
import React, { useEffect, useState } from 'react';
import { FiBell, FiSettings } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../app/store';
import logo from '../assets/logo.svg';
import profileplaceholder from '../assets/placeholderprofile.png';
import { fetchNotifications, fetchUnreadCount } from '../features/notificationSlider/notificationsSlice';
import { BASE_URL } from '../utils/constants';
import { getAuthContext } from '../utils/jwt';
import NotificationSlider from './NotificationSlider';
import { toast } from 'react-toastify';


const FiBellComponent = FiBell as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiSettingsComponent = FiSettings as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const Header: React.FC = () => {
  // const user = useSelector((state: RootState) => state.auth.user);
  const profileData = useSelector((state: RootState) => state.profile);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isNotifOpen, setIsNotifOpen] = useState(false);

  // Get notifications from Redux store
  const notifications = useSelector(
    (state: RootState) => state.notifications.items
  );

  // Calculate unread count from notifications data
  const unreadCount = notifications.filter(n => !n.read).length;

  const [profilePhoto, setProfilePhoto] = useState<string>(profileplaceholder);

  // Fetch unread count from API
  const fetchUnreadCountFromAPI = async () => {
    try {
      await dispatch(fetchUnreadCount()).unwrap();
    } catch (error) {
      // console.error('Failed to fetch unread count:', error);
      toast.error('Failed to fetch unread count');
    }
  };

  // Fetch notifications from API
  const fetchNotificationsFromAPI = async () => {
    try {
      await dispatch(fetchNotifications()).unwrap();
    } catch (error) {
      // console.error('Failed to fetch notifications:', error);
      toast.error('Failed to fetch notifications');
    }
  };

  const fetchProfilePhotoFromAPI = async (token: string, tenantId: string, userId: string): Promise<string> => {
    try {
      const response = await fetch(`${BASE_URL}/v1/profile/me/photo`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) return profileplaceholder;

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      // console.error('Failed to fetch profile photo:', error);
      toast.error('Failed to fetch profile photo');
      return profileplaceholder;
    }
  };

  useEffect(() => {
    // Fetch notifications and unread count when component mounts
    fetchNotificationsFromAPI();
    fetchUnreadCountFromAPI();

    // Set up interval to fetch data periodically (every 60 seconds)
    // const intervalId = setInterval(() => {
    //   fetchNotificationsFromAPI();
    //   fetchUnreadCountFromAPI();
    // }, 60000);

    // return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const initializeProfilePhoto = async () => {
      const { token, tenantId, userId } = getAuthContext();

      if (!token) {
        setProfilePhoto(profileplaceholder);
        return;
      }

      // First try to get from API (especially important after login)
      const apiPhoto = await fetchProfilePhotoFromAPI(token, tenantId, userId || '');

      if (apiPhoto !== profileplaceholder) {
        setProfilePhoto(apiPhoto);
        // Also update localStorage so other components can use it
        const savedData = localStorage.getItem('profileData');
        if (savedData) {
          const parsed = JSON.parse(savedData);
          parsed.profilePhoto = apiPhoto;
          localStorage.setItem('profileData', JSON.stringify(parsed));
        }
      } else {
        // Fallback to existing logic (Redux / localStorage)
        const savedProfileData = localStorage.getItem('profileData');
        if (savedProfileData) {
          try {
            const parsed = JSON.parse(savedProfileData);
            setProfilePhoto(parsed.profilePhoto || profileplaceholder);
          } catch (e) {
            setProfilePhoto(profileData?.profilePhoto || profileplaceholder);
          }
        } else {
          setProfilePhoto(profileData?.profilePhoto || profileplaceholder);
        }
      }
    };

    initializeProfilePhoto();

    // Keep your original listeners for updates
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'profileData') {
        const saved = localStorage.getItem('profileData');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setProfilePhoto(parsed.profilePhoto || profileplaceholder);
          } catch { }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      const saved = localStorage.getItem('profileData');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.profilePhoto && parsed.profilePhoto !== profilePhoto) {
            setProfilePhoto(parsed.profilePhoto);
          }
        } catch { }
      }
    }, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [profileData]);

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleNotificationClick = () => {
    setIsNotifOpen(true);
    // Refresh notifications and unread count when opening the slider
    fetchNotificationsFromAPI();
    fetchUnreadCountFromAPI();
  };

  return (
    <>
      <header className='flex justify-between items-center p-4 bg-white'>
        <div className='flex items-center'>
          <img src={logo} alt='AutoPath Logo' className='h-8' />
        </div>
        <div className='flex items-center space-x-4'>
          <div className='flex space-x-2'>
            <div
              className='relative flex items-center justify-center cursor-pointer px-2 group'
              onClick={handleNotificationClick}
            >
              <FiBellComponent size={22} className='text-gray-500' />
              {unreadCount > 0 ? (
                <span className='absolute -top-0.5 right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center'>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : (
                <span className='absolute -top-0.5 right-0.5 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center'>
                  {unreadCount}
                </span>
              )}
              <span className='absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                Notifications
              </span>
            </div>

            <div
              className='relative flex items-center justify-center px-3 cursor-pointer group'
              onClick={handleSettingsClick}
            >
              <FiSettingsComponent size={20} className='text-gray-500' />
              <span className='absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                Settings
              </span>
            </div>
            <div
              className='relative flex items-center justify-center px-4 border-l-2 border-gray cursor-pointer'
              onClick={handleProfileClick}
            >
              <img
                src={profilePhoto}
                alt='Profile'
                className='w-8 h-8 rounded-full object-cover'
                onError={(e) => {
                  e.currentTarget.src = profileplaceholder;
                }}
              />
            </div>
          </div>
        </div>
      </header>

      <NotificationSlider
        isOpen={isNotifOpen}
        onClose={() => setIsNotifOpen(false)}
      />
    </>
  );
};

export default Header;